import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { apiRequest, getToken, devError } from "@ridecloud/shared";
import {
  Search,
  MapPin,
  Calendar,
  ArrowLeft,
  Eye,
  Car,
  Users,
  Clock,
  Sparkles,
  Navigation,
  Shield,
  ArrowLeftRight,
  ArrowDownUp,
} from "lucide-react";

function SearchRide() {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pickupError, setPickupError] = useState("");
  const [dropoffError, setDropoffError] = useState("");

  // States for suggestions
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [cities, setCities] = useState([]); // Store fetched cities
  const isFormValid = pickup && destination && date;

  useEffect(() => {
    if (localStorage.getItem("rideData") != null) {
      const rideData = JSON.parse(localStorage.getItem("rideData"));
      setPickup(rideData.pickup);
      setDestination(rideData.destination);
      setDate(rideData.date);
    }

    const fetchCities = async () => {
      try {
        const response = await apiRequest(async (APIv1) =>
          APIv1.get("/user/dashboard/get_cities", {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
          })
        );
        setCities(response.data);
      } catch (error) {
        devError("Error fetching cities:", error);
      }
    };

    fetchCities();
  }, []);

  // Handle pickup input change
  const handlePickupChange = (value) => {
    setPickup(value);
    if (value) {
      const filteredSuggestions = cities.filter((city) =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setPickupSuggestions(filteredSuggestions);
    } else {
      setPickupSuggestions([]);
    }
  };

  // Handle destination input change
  const handleDestinationChange = (value) => {
    setDestination(value);
    if (value) {
      const filteredSuggestions = cities.filter((city) =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setDestinationSuggestions(filteredSuggestions);
    } else {
      setDestinationSuggestions([]);
    }
  };

  // Handle Ride Search
  const handleSearch = async (e) => {
    e.preventDefault();
    setPickupError("");
    setDropoffError("");

    if (!cities.includes(pickup)) {
      setPickupError("Please select a valid city from the suggestions.");
      return;
    }
    if (!cities.includes(destination)) {
      setDropoffError("Please select a valid city from the suggestions.");
      return;
    }

    setLoading(true);
    setError("");

    localStorage.setItem(
      "rideData",
      JSON.stringify({ pickup, destination, date })
    );

    if (isFormValid) {
      navigate(
        `/view-rides?from=${encodeURIComponent(pickup)}&to=${encodeURIComponent(
          destination
        )}&date=${encodeURIComponent(date)}`
      );
    }
  };

  const handleViewAllRides = () => {
    if (!date) {
      setError("Please select a date to view all active rides.");
      return;
    }
    navigate(`/view-rides?date=${encodeURIComponent(date)}`);
  };
  const swapHandler = () => {
    setPickup(destination);
    setDestination(pickup);

    setPickupError(dropoffError);
    setDropoffError(pickupError);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 relative overflow-hidden ">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-green-200/30 to-emerald-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-emerald-200/20 to-green-300/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-green-100/20 to-emerald-100/20 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Header with enhanced gradient and glass effect */}
      <div
        className="relative bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-8 shadow-2xl backdrop-blur-sm"
        style={{
          paddingTop: Capacitor.isNativePlatform()
            ? "env(safe-area-inset-top)"
            : undefined,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/90 to-teal-600/90 backdrop-blur-sm"></div>
        <div className="relative max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div
              onClick={() => navigate("/dashboard")}
              className="group w-10 h-10 rounded-2xl flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <ArrowLeft className="w-6 h-6 text-white group-hover:-translate-x-1 transition-transform duration-300" />
            </div>

            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-white-900">
                Find Your Perfect Ride
              </h1>
              <p className="text-sm text-white-600">
                Discover amazing journeys to your destination
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-12">
        {/* Enhanced Main Search Card */}
        <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-10 border border-emerald-100/50 overflow-hidden">
          {/* Card Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-green-50/30 pointer-events-none"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-emerald-100/30 to-transparent rounded-full blur-2xl"></div>

          <div className="relative text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
              <Navigation className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-700 to-green-700 bg-clip-text text-transparent mb-3">
              Where are you going?
            </h2>
            <p className="text-gray-600 text-sm md:text">
              Search for available rides to your destination
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-8">
            <div className="relative flex flex-col md:flex-row md:items-start gap-4 md:gap-3">
              {/* Enhanced Pickup Location */}
              <div className="flex-1">
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  <span>Pickup Location</span>
                </label>
                <div className="relative group">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-emerald-500 group-focus-within:text-emerald-600 transition-colors duration-200">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={pickup}
                    onChange={(e) => handlePickupChange(e.target.value)}
                    placeholder="Enter pickup location"
                    className="w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-all duration-300 text-gray-800 bg-gray-50/50 focus:bg-white text-sm font-medium shadow-sm focus:shadow-md hover:border-emerald-300"
                  />
                  {pickupSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border border-gray-200 rounded-xl mt-1 shadow-xl z-50 max-h-48 overflow-y-auto">
                      {pickupSuggestions.map((city, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setPickup(city);
                            setPickupSuggestions([]);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-emerald-50 transition-all duration-200 border-b border-gray-100 last:border-b-0 text-gray-700 hover:text-emerald-700 text-sm font-medium group"
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full group-hover:scale-110 transition-transform duration-200"></div>
                            <MapPin className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" />
                            <span>{city}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {pickupError && (
                  <p className="text-red-500 text-xs mt-1 flex items-center space-x-1 bg-red-50 px-2 py-1.5 rounded-md">
                    <span className="text-red-400">⚠️</span>
                    <span className="font-medium">{pickupError}</span>
                  </p>
                )}
              </div>
              {/* Swap Button */}
              <div className="flex justify-center md:items-end md:mb-1 mt-4 md:mt-0">
                {/* Swap Button - Vertical for mobile, Horizontal for desktop */}
                <button
                  type="button"
                  onClick={swapHandler}
                  className="hidden md:flex w-10 h-10 self-center rounded-full
             bg-white border-2 border-gray-300
             items-center justify-center
             hover:border-emerald-400 hover:shadow-md transition"
                  aria-label="Swap locations"
                >
                  <ArrowLeftRight className="w-5 h-5 text-gray-600 hover:rotate-180 transition-transform" />
                </button>

                <button
                  type="button"
                  onClick={swapHandler}
                  className="absolute md:hidden right-3 top-1/2 -translate-y-1/2
             w-9 h-9 rounded-full bg-white border border-gray-300
             flex items-center justify-center shadow-md
             hover:border-emerald-400 transition"
                  aria-label="Swap locations"
                >
                  <ArrowDownUp className="w-4 h-4 text-gray-600 hover:rotate-180 transition-transform" />
                </button>
              </div>

              {/* Enhanced Destination */}
              <div className="flex-1">
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  <span>Destination</span>
                </label>
                <div className="relative group">
                  <div className="absolute left-5 top-1/2 transform -translate-y-1/2 text-orange-500 group-focus-within:text-orange-600 transition-colors duration-200">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={destination}
                    onChange={(e) => handleDestinationChange(e.target.value)}
                    placeholder="Enter destination"
                    className="w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300 text-gray-800 bg-gray-50/50 focus:bg-white text-sm font-medium shadow-sm focus:shadow-md hover:border-orange-300"
                  />

                  {destinationSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border border-gray-200 rounded-xl mt-1 shadow-xl z-50 max-h-48 overflow-y-auto">
                      {destinationSuggestions.map((city, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setDestination(city);
                            setDestinationSuggestions([]);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-orange-50 transition-all duration-200 border-b border-gray-100 last:border-b-0 text-gray-700 hover:text-orange-700 text-sm font-medium group"
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-orange-400 rounded-full group-hover:scale-110 transition-transform duration-200"></div>
                            <MapPin className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-500 transition-colors duration-200" />
                            <span>{city}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {dropoffError && (
                  <p className="text-red-500 text-xs mt-1 flex items-center space-x-1 bg-red-50 px-2 py-1.5 rounded-md">
                    <span className="text-red-400">⚠️</span>
                    <span className="font-medium">{dropoffError}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Enhanced Date */}
            <div className="space-y-1">
              <label className="block text-[10px] sm:text-xs font-bold text-gray-700 flex items-center space-x-1">
                <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-500" />
                <span>Travel Date</span>
              </label>
              <div className="relative group">
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-blue-500 group-focus-within:text-blue-600 transition-colors duration-200">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full pl-10 pr-2 py-2 sm:pl-12 sm:pr-3 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 bg-gray-50/50 focus:bg-white text-[10px] sm:text-sm font-medium shadow-sm focus:shadow-sm hover:border-blue-300"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {/* Enhanced Search Button */}
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className={`relative w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-xl transition-all duration-500 flex items-center justify-center space-x-2 sm:space-x-4 overflow-hidden group ${
                isFormValid && !loading
                  ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:via-teal-700 hover:to-teal-700 shadow-lg sm:shadow-xl hover:shadow-2xl transform hover:-translate-y-1 hover:scale-[1.02]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {isFormValid && !loading && (
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-400 opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              )}
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-6 sm:w-6 border-2 sm:border-3 border-white border-t-transparent"></div>
                  <span className="relative z-10 text-xs sm:text-base">
                    Searching for rides...
                  </span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 sm:w-6 sm:h-6 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  <span className="relative z-10 text-sm sm:text-base">
                    Search Rides
                  </span>
                </>
              )}
            </button>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-3 py-2 sm:px-6 sm:py-4 rounded-md sm:rounded-2xl text-center font-medium text-xs sm:text-sm shadow-sm">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-gray-700 text-sm sm:text-xl font-semibold">
                OR
              </span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          </form>
          {/* Enhanced View All Active Rides Button */}
          <div className="mt-5 mb-8 sm:mb-12 flex justify-center">
            <button
              onClick={handleViewAllRides}
              className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-2 border-transparent px-4 sm:px-8 py-2 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-lg hover:from-emerald-600 hover:to-teal-700 hover:border-emerald-700 transition-all duration-500 shadow-md sm:shadow-lg hover:shadow-xl transform hover:-translate-y-1 sm:hover:-translate-y-2 flex items-center space-x-2 sm:space-x-3 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <Eye className="w-4 h-4 sm:w-6 sm:h-6 relative z-10 group-hover:scale-110 transition-transform duration-300" />
              <span className="relative z-10 text-xs sm:text-base">
                View All Active Rides
              </span>
              <div className="absolute -right-1 -top-1 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full animate-ping group-hover:bg-emerald-300"></div>
            </button>
          </div>

          {/* Enhanced Popular Destinations */}
          <h3 className="mt-16 text-xl sm:text-2xl font-bold text-gray-800 mb-6 sm:mb-8 text-center flex items-center justify-center space-x-1 sm:space-x-2">
            <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-500" />
            <span>Popular Destinations</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {["Jammu", "Srinagar", "Jagti Township", "Anantnag"].map(
              (place) => (
                <button
                  key={place}
                  onClick={() => setDestination(place)}
                  className="group relative bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-emerald-200 text-emerald-700 py-2 sm:py-4 px-3 sm:px-5 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm hover:from-emerald-600 hover:to-green-600 hover:text-white hover:border-emerald-600 transition-all duration-400 transform hover:-translate-y-1 hover:shadow-md sm:hover:shadow-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                  <div className="relative flex items-center justify-center space-x-1 sm:space-x-2">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-[10px] sm:text-sm">{place}</span>
                  </div>
                </button>
              )
            )}
          </div>

          {/* Enhanced Features Section */}
          <div className="grid md:grid-cols-3 gap-4 sm:gap-8 mt-8 sm:mt-12">
            {[
              {
                icon: Users,
                title: "Shared Rides",
                desc: "Split costs with fellow travelers",
              },
              {
                icon: Clock,
                title: "Flexible Timing",
                desc: "Choose your preferred departure",
              },
              {
                icon: Shield,
                title: "Safe & Secure",
                desc: "Verified drivers and secure payments",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group text-center p-4 sm:p-6 bg-gradient-to-br from-emerald-50 to-white rounded-xl sm:rounded-2xl border-2 border-emerald-100 hover:border-emerald-300 transition-all duration-400 hover:shadow-md sm:hover:shadow-lg transform hover:-translate-y-0.5 sm:hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-105 transition-transform duration-300 shadow-md sm:shadow-lg">
                  <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <h4 className="font-bold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-lg">
                  {title}
                </h4>
                <p className="text-gray-600 font-medium text-[10px] sm:text-sm">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchRide;
